/**
 * 会话管理 Composable
 * 负责生成和管理会话 ID、上下文持久化
 */

import { ref, computed } from 'vue'
import { useCookie } from '#app'
import { StorageKey } from '~/enums'
import { dateId } from "~/utils";

export interface ChatSession {
  id: string
  messages: Array<{
    role: string
    content: string
    tool_calls?: any[]
    timestamp: number
  }>
  createdAt: number
  updatedAt: number
}

/**
 * 初始化新会话
 * @returns 
 */
export function useChatSession() {
  // 使用 useCookie 替代 useStorage，避免 SSR hydration mismatch
  // sessionId 只存储当前活跃的会话 ID（轻量数据，适合 Cookie）
  const sessionId = useCookie<string>(StorageKey.CURRENT_SESSION_ID, {
    maxAge: 60 * 60 * 24 * 365, // 1年有效期
    default: () => ''
  })
  
  // sessionHistory 存储会话历史列表（使用 sessionStorage 避免 Cookie 大小限制）
  // 由于 sessionHistory 可能较大，在组件中使用 ref 而非 Cookie
  const sessionHistory = ref<ChatSession[]>([])
  
  // 当前会话的消息列表
  const currentMessages = ref<Array<any>>([])

  /**
   * 生成新的会话 ID
   */
  const generateSessionId = (): string => {
    return `session_${dateId()}`
  }

  /**
   * 初始化新会话
   */
  const initSession = async () => {
    const newId = generateSessionId()
    sessionId.value = newId // 生成新的会话 ID
    currentMessages.value = []
    
    console.log('🆕 新会话已创建，ID为:', newId)
    
    // 同步到数据库（异步执行，不阻塞）
    try {
      const userId = useAnonymousUser().getAnonymousId()
      // 🔗调用接口
      await $fetch('/api/session/sessions', {
        method: 'POST',
        body: {
          userId,
          sessionId: newId,
          title: '新对话1'
        }
      })
      console.log('💾 会话已保存到数据库:', newId)
    } catch (error) {
      console.warn('⚠️ 保存会话到数据库失败:', error)
    }
    
    return newId
  }

  /**
   * 先创建会话再更新标题
   * 更新会话标题（使用第一条用户消息），后端会自动处理"创建或更新"逻辑，无需前端判断
   */
  const updateSessionTitle = async (
    firstMessage: string,
    options: { isTitleAutoGenerated?: boolean } = {},
  ) => {
    if (!sessionId.value || !firstMessage) return
    
    try {
      const userId = useAnonymousUser().getAnonymousId()
      
      // 截取前20个字符作为会话标题
      const title = firstMessage.length > 20 
        ? firstMessage.substring(0, 20) + '...' 
        : firstMessage
      
      // console.log('📝 尝试更新会话标题:', { sessionId: sessionId.value, userId, title })
      
      // 🔗调用接口: 更新或创建会话标题（后端自动处理）
      await $fetch('/api/session/title', {
        method: 'patch',
        body: {
          sessionId: sessionId.value,
          userId,
          title,
          isTitleAutoGenerated: options.isTitleAutoGenerated ?? true
        }
      })
      
      console.log('✅ 会话标题已更新:', title)
    } catch (error: any) {
      console.warn('⚠️ 更新会话标题失败:', error)
    }
  }

  /**
   * 设置会话 ID（用于从数据库恢复或切换会话）
   */
  const setSessionId = (id: string) => {
    sessionId.value = id
  }

  /**
   * 添加消息到当前会话
   */
  const addMessage = (role: string, content: string, metadata?: any) => {
    const message = {
      role,
      content,
      timestamp: Date.now(),
      ...metadata
    }
    
    currentMessages.value.push(message)
    
    // 更新会话最后修改时间
    updateSessionTimestamp()
    
    return message
  }

  /**
   * 清空当前会话
   */
  const clearSession = () => {
    currentMessages.value = []
    
    // 可选：保存当前会话到历史记录
    if (sessionId.value && currentMessages.value.length > 0) {
      saveSessionToHistory()
    }
    
    // 创建新会话
    initSession()
    
    console.log('🗑️ 会话已清空')
  }

  /**
   * 保存会话到历史记录
   */
  const saveSessionToHistory = () => {
    const session: ChatSession = {
      id: sessionId.value,
      messages: currentMessages.value,
      createdAt: Date.now(),
      updatedAt: Date.now()
    }
    
    sessionHistory.value.unshift(session)
    
    // 限制历史记录数量（最多 50 个会话）
    if (sessionHistory.value.length > 50) {
      sessionHistory.value = sessionHistory.value.slice(0, 50)
    }
    
    console.log('💾 会话已保存到历史记录')
  }

  /**
   * 更新会话时间戳
   */
  const updateSessionTimestamp = () => {
    // 可以在这里实现更复杂的持久化逻辑
  }

  /**
   * 加载历史会话
   */
  const loadSession = (id: string) => {
    const session = sessionHistory.value.find(s => s.id === id)
    
    if (session) {
      sessionId.value = session.id
      currentMessages.value = session.messages
      
      console.log('📂 会话已加载:', id)
      return true
    }
    
    return false
  }

  /**
   * 删除历史会话
   */
  const deleteSession = (id: string) => {
    const index = sessionHistory.value.findIndex(s => s.id === id)
    
    if (index !== -1) {
      sessionHistory.value.splice(index, 1)
      console.log('🗑️ 会话已从历史记录删除:', id)
    }
  }

  return {
    sessionId,
    currentMessages,
    sessionHistory,
    initSession,
    updateSessionTitle, // 更新会话标题
    setSessionId,
    addMessage,
    clearSession,
    loadSession,
    deleteSession,
    saveSessionToHistory
  }
}




















